/**
 * The React binding for {@link WorkspaceStore}.
 *
 * Reads go through `useSyncExternalStore` with **per-slice selectors**, which is
 * the whole reason the editor stays fast while typing: `applyCommand` rebuilds
 * only the documents a command touched, so a selector returning
 * `state.recipes[id]` yields an identical reference for every unrelated change
 * and the subscriber does not re-render. A component that reads one recipe is
 * insulated from edits to another, from settings changes, and from a second
 * tab's writes.
 *
 * Selectors that *derive* a value (a sorted list, a count) cannot rely on
 * reference identity, so {@link useWorkspaceSelector} caches the last computed
 * value against the state it came from and additionally accepts an equality
 * function. Without that, `useSyncExternalStore` would see a fresh array on
 * every `getSnapshot` call and loop.
 *
 * ## The one rule this file installs
 *
 * A **guest** has no server. Nothing a signed-out workspace dispatches ever
 * leaves the device, so the Durable Object that judges every other account's
 * recipe, blend and journal count never sees it — and the free tier's ten
 * recipes were, for a guest, a number the sidebar displayed rather than a limit
 * anything enforced. {@link WorkspaceBinding} therefore installs the *same*
 * function the object runs (`lib/plan-cap.ts`) as the store's dispatch guard
 * while the identity is `null`, and lifts it the moment there is a user id,
 * because from then on the server is the only judge and a second opinion on the
 * client could only disagree with it.
 *
 * ## Two stores
 *
 * What the UI holds is not the workspace's {@link WorkspaceStore} but a
 * {@link RoutedWorkspaceStore} around it (PLAN-COLLAB C2): the workspace with
 * every shared recipe's room laid over it, routing each write to whichever of
 * them it is about. The raw store is still what persistence, the guard and the
 * workspace's sync client attach to, because their invariants are about one
 * store; {@link useRawWorkspaceStore} is for them and nobody else.
 */

import type {
  BatchDoc,
  BlendDoc,
  Command,
  CustomIngredientDoc,
  EquipmentProfileDoc,
  FermentationProfileDoc,
  InventoryItemDoc,
  JournalDoc,
  MashProfileDoc,
  MiscProfileDoc,
  RecipeDoc,
  SavedWaterProfileDoc,
  SharedRecipeRef,
  UnitPreferences,
  WorkspaceSettings,
  WorkspaceState,
} from '@beerwright/commands'; // prettier-ignore
import { capRecipeCount, emptyWorkspace, isSharedEquipmentId } from '@beerwright/commands';
import type { ReactNode } from 'react';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'; // prettier-ignore
import { journalReferences } from '@/lib/journal-search';
import { naturalCompare, naturalCompareBy } from '@/lib/natural-sort';
import { BLEND_LIMIT, DEFAULT_ENTITLEMENT, JOURNAL_LIMIT } from '@/lib/plan';
import { planCapRejection } from '@/lib/plan-cap';
import { reportError } from '@/lib/posthog';
import type { PresenceAt } from '@/sync/protocol';
// The reconciliation primitive the sync client uses, so the two paths that fold
// a base and a set of local commands together cannot drift apart. `reconcile.ts`
// depends on nothing but the commands package.
import { replay, sameWorkspace } from '@/sync/reconcile';
import type { Peer } from '@/sync/room-sources';
import type { Persistence } from './persistence';
import {
  channelNameFor,
  createPersistence,
  HYDRATION_TIMEOUT_MS,
  snapshotKeyFor,
} from './persistence';
import type { WorkspaceStoreApi } from './routed-store';
import { RoutedWorkspaceStore } from './routed-store';
import { WorkspaceStore } from './workspace-store';

interface WorkspaceContextValue {
  /** What the UI reads and dispatches to: the workspace and its rooms, as one. */
  readonly store: RoutedWorkspaceStore;
  /** The workspace's own store, for what attaches to it alone. */
  readonly workspace: WorkspaceStore;
  /** False until the saved workspace has been read back from IndexedDB. */
  readonly hydrated: boolean;
  /** Whose workspace this is: a user id, or `null` for the signed-out one. */
  readonly identity: string | null;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

/** Options for {@link WorkspaceProvider}. Tests inject a store and skip persistence. */
export interface WorkspaceProviderProps {
  readonly children: ReactNode;
  /** Use this store instead of creating one. */
  readonly store?: WorkspaceStore;
  /** Use this persistence instead of the default IndexedDB one; `null` disables it. */
  readonly persistence?: Persistence | null;
  /**
   * Whose workspace to open: a signed-in user's id, or `null` for guest.
   *
   * Changing it swaps the whole storage binding — see {@link WorkspaceBinding}.
   */
  readonly identity?: string | null;
}

/**
 * The store, and a storage binding for one identity.
 *
 * The **store** outlives identity changes: it is what every component holds a
 * reference to, and what the sync client is attached to. The **binding** does
 * not — it is remounted by `key` when the identity changes, which disposes the
 * old channel and debounce, flushes the outgoing workspace to its own key, and
 * hydrates the incoming one from scratch. Doing it with a key rather than with
 * effects is what keeps "sign out, and the previous account's recipes are gone
 * from the screen" a structural property rather than a cleanup step someone
 * has to remember.
 */
export function WorkspaceProvider({
  children,
  store,
  persistence,
  identity = null,
}: WorkspaceProviderProps) {
  const [instance] = useState(() => store ?? new WorkspaceStore());
  const [routed] = useState(() => new RoutedWorkspaceStore(instance));
  return (
    <WorkspaceBinding
      key={identity ?? 'guest'}
      store={instance}
      routed={routed}
      persistence={persistence}
      identity={identity}
    >
      {children}
    </WorkspaceBinding>
  );
}

interface WorkspaceBindingProps {
  readonly children: ReactNode;
  readonly store: WorkspaceStore;
  readonly routed: RoutedWorkspaceStore;
  /** `undefined` means "make one"; `null` means "no persistence at all". */
  readonly persistence: Persistence | null | undefined;
  readonly identity: string | null;
}

function WorkspaceBinding({
  children,
  store,
  routed,
  persistence,
  identity,
}: WorkspaceBindingProps) {
  const instance = store;
  /** True when this binding owns the layer's lifetime; false when one was injected. */
  const managed = persistence === undefined;
  const [layer, setLayer] = useState<Persistence | null>(managed ? null : persistence);
  // `persistence === null` is "no persistence at all", and there is nothing to
  // wait for. A managed binding starts un-hydrated and its creating effect,
  // below, is what gets it going.
  const [hydrated, setHydrated] = useState(persistence === null);

  /**
   * Hydrate, then keep writing. Both effects are deliberately in one place so
   * the write subscription cannot start before the read has landed and
   * overwrite the saved workspace with an empty one.
   *
   * The read is **raced against a timeout** rather than simply awaited, because
   * a storage read that never answers is a real state and not a hypothetical
   * one: {@link HYDRATION_TIMEOUT_MS} explains how Chromium gets there. Losing
   * that race is treated as "nothing saved" — the same path an account with no
   * record takes — so the brewer gets an empty workspace they can work in
   * instead of a splash screen that never leaves, and a signed-in workspace is
   * refilled over the sync socket the way it is on a fresh device — by the
   * client's first catch-up, which recomputes whatever the store holds rather
   * than only what the server has changed. See `sync-client.ts`.
   *
   * Whichever of the two arrives first wins outright. A read that turns up
   * afterwards is dropped on the floor: by then the workspace is live, possibly
   * edited, possibly already backfilled from the server, and replacing it with
   * a snapshot from before the boot would be a silent revert. `settled` is what
   * makes that a no-op, and unmounting sets it for the same reason the old
   * `cancelled` flag did.
   */
  useEffect(() => {
    if (layer === null) return;
    let settled = false;

    const open = (saved: WorkspaceState | null) => {
      if (settled) return;
      settled = true;
      // `?? emptyWorkspace()` rather than "leave it alone": this binding may be
      // replacing another identity's, and an account with nothing saved must
      // render as empty, not as whoever was signed in a moment ago.
      instance.replaceState(saved ?? emptyWorkspace());
      setHydrated(true);
    };

    const giveUp = setTimeout(() => {
      if (settled) return;
      console.warn(
        `[beerwright] the saved workspace did not load within ${HYDRATION_TIMEOUT_MS} ms; ` +
          'opening an empty one. Nothing has been deleted — it is still on disk.',
      );
      // Reported as well as logged: the stall this covers happens on somebody
      // else's phone, on a tab that was backgrounded, and an empty workspace is
      // not what it looks like from the outside. The console line is for
      // whoever has the tab open; this is the only way we hear about it.
      reportError(new Error('workspace hydration timed out'));
      open(null);
    }, HYDRATION_TIMEOUT_MS);

    void layer.load().then(open);

    return () => {
      settled = true;
      clearTimeout(giveUp);
    };
  }, [instance, layer]);

  /**
   * Keep writing, and answer another tab by *reconciling* rather than replacing.
   *
   * Another tab's ping used to be answered by reading the snapshot and calling
   * `replaceState` with it, flat. Writes are debounced, so that snapshot is up
   * to `DEBOUNCE_MS` older than what this tab has on screen, and the
   * replacement silently undid everything dispatched inside the window. Two
   * tabs on the same account never have to touch the same recipe for that to
   * bite: they share one document, and the loser is whichever had an unwritten
   * edit when the other saved. It reported nothing, because as far as this path
   * knew it was catching up.
   *
   * The fix is the split the sync client has always used, and deliberately the
   * *same primitive*: the snapshot is the confirmed base, the commands not yet
   * inside it are pending, and the screen is `replay(base, pending)`. `mark`
   * pairs `schedule` with `onWritten` so "not yet inside it" is exact rather
   * than a guess about timing.
   *
   * Two things this does not do. It does not resolve two tabs that have
   * genuinely diverged over minutes, each holding committed writes the other
   * never saw — whole-snapshot last-write-wins still decides that, and the
   * server is what actually fixes it. And a pending command the other tab's
   * snapshot has made inapplicable (they deleted the recipe this one is
   * editing) is dropped by the replay without a word, as it was before.
   */
  useEffect(() => {
    if (layer === null || !hydrated) return;

    // Applied here, not yet known to be in the snapshot on disk.
    let unsaved: { readonly mark: number; readonly command: Command }[] = [];
    let mark = 0;

    const unobserve = instance.observeCommands((command) => {
      mark += 1;
      unsaved.push({ mark, command });
    });
    const unsubscribeStore = instance.subscribe(() => layer.schedule(instance.getState(), mark));
    const unsubscribeWritten = layer.onWritten((written) => {
      unsaved = unsaved.filter((entry) => entry.mark > written);
    });

    const unsubscribeExternal = layer.onExternalChange(() => {
      void layer.load().then((saved) => {
        if (saved === null) return;
        const pending = unsaved.map((entry) => entry.command);
        const next = pending.length === 0 ? saved : replay(saved, pending).state;
        // A ping that turns out to carry nothing new must not touch the store:
        // `replaceState` clears the undo stack — correctly, when the state
        // really moved — and a tab flushing as it hides is the common ping, not
        // the rare one. Clearing a brewer's undo every time they switched tabs
        // would be a bug of its own. The compare is a whole document, on a path
        // that has just done an IndexedDB read, so it is not the expensive part.
        if (sameWorkspace(next, instance.getState())) return;
        instance.replaceState(next);
      });
    });

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') void layer.flush();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onVisibilityChange);

    return () => {
      unobserve();
      unsubscribeStore();
      unsubscribeWritten();
      unsubscribeExternal();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onVisibilityChange);
      void layer.flush();
    };
  }, [instance, layer, hydrated]);

  /**
   * A guest's caps, enforced locally — see the file header.
   *
   * The free tier's numbers rather than an entitlement: a guest has never been
   * granted anything, and {@link DEFAULT_ENTITLEMENT} is what the rest of the
   * app already shows them. A signed-in binding clears the guard instead of
   * setting a bigger one, so the "New recipe" button keeps the single
   * authoritative code path `lib/plan.ts` argues for.
   */
  useEffect(() => {
    if (identity !== null) {
      instance.setGuard(null);
      return;
    }
    instance.setGuard((state, command) =>
      planCapRejection(state, command, {
        recipeLimit: DEFAULT_ENTITLEMENT.recipeLimit,
        blendLimit: BLEND_LIMIT,
        journalLimit: JOURNAL_LIMIT,
      }),
    );
    return () => {
      instance.setGuard(null);
    };
  }, [instance, identity]);

  /**
   * Own the layer's lifetime: create it here, dispose it in this effect's
   * cleanup, and never anywhere else.
   *
   * **Not `useState(() => createPersistence(…))`.** A layer created during
   * render outlives its own disposal under React StrictMode, which mounts,
   * tears down and mounts again in development: the first teardown closed the
   * one and only `BroadcastChannel`, and the second mount went on using the
   * closed object. Cross-tab sync was therefore dead for the whole of
   * `pnpm dev` — and worked perfectly in production, where StrictMode's double
   * invoke does not happen, which is exactly the shape of bug that survives for
   * months.
   *
   * Creating inside the effect makes creation and disposal the same lifecycle,
   * so the second mount builds a fresh layer with a fresh channel. Nothing else
   * has to know: `layer` simply arrives one render later, and the two effects
   * above already skip a `null` one.
   *
   * Declared **last** so its cleanup runs last — the debounced write above must
   * be flushed before the channel and the timer are torn down. That ordering is
   * what makes switching identity safe: the outgoing account's workspace is
   * committed under its own key before the incoming one is read.
   */
  const key = snapshotKeyFor(identity);
  const channelName = channelNameFor(identity);
  useEffect(() => {
    if (!managed) return;
    const created = createPersistence({ key, channelName });
    setLayer(created);
    return () => {
      // Clear only if it is still ours: a StrictMode remount may already have
      // installed the replacement by the time this runs.
      setLayer((current) => (current === created ? null : current));
      void created.flush().finally(() => created.dispose());
    };
  }, [managed, key, channelName]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({ store: routed, workspace: instance, hydrated, identity }),
    [routed, instance, hydrated, identity],
  );

  return <WorkspaceContext value={value}>{children}</WorkspaceContext>;
}

function useWorkspaceContext(): WorkspaceContextValue {
  const context = use(WorkspaceContext);
  if (context === null) throw new Error('useWorkspace must be used inside a <WorkspaceProvider>');
  return context;
}

/** The store itself — for dispatching. Never changes identity. */
export function useWorkspaceStore(): WorkspaceStoreApi {
  return useWorkspaceContext().store;
}

/**
 * The workspace's own store, without the rooms.
 *
 * For the sync provider, whose workspace client attaches to one store and
 * whose invariants are about that one (`sync-context.tsx`). Anything that reads
 * or writes on the brewer's behalf wants {@link useWorkspaceStore}.
 */
export function useRawWorkspaceStore(): WorkspaceStore {
  return useWorkspaceContext().workspace;
}

/** The routed store, for the sync provider to hand the rooms to. */
export function useRoutedWorkspaceStore(): RoutedWorkspaceStore {
  return useWorkspaceContext().store;
}

const noopSubscribe = () => () => undefined;

/**
 * Whether `recipeId` is a shared recipe whose room has not caught up yet
 * (D-CO11c).
 *
 * Between the workspace learning it is in a room and the room's first answer,
 * the recipe exists nowhere on this device, and an id page must call that
 * loading rather than missing. `false` outside a provider, like
 * `useWorkspaceSettled`.
 */
export function useSharedRecipeLoading(recipeId: string | undefined): boolean {
  const routed = use(WorkspaceContext)?.store ?? null;
  const get = useCallback(
    () =>
      routed !== null &&
      recipeId !== undefined &&
      Object.hasOwn(routed.getState().sharedRecipes, recipeId) &&
      !routed.isRoomSettled(recipeId),
    [routed, recipeId],
  );
  return useSyncExternalStore(routed?.subscribe ?? noopSubscribe, get, get);
}

const NO_PEERS: readonly Peer[] = [];

/**
 * The others in `recipeId`'s room now, never me (PLAN-COLLAB D2). Empty for a
 * recipe of my own, before the room has a socket, and outside a provider.
 *
 * Stable between rosters: a room replaces its `peers` array only when a
 * roster arrives, and the routed store's subscription carries that change.
 */
export function useRoomPeers(recipeId: string): readonly Peer[] {
  const routed = use(WorkspaceContext)?.store ?? null;
  const get = useCallback(() => routed?.room(recipeId)?.peers ?? NO_PEERS, [routed, recipeId]);
  return useSyncExternalStore(routed?.subscribe ?? noopSubscribe, get, get);
}

/** What {@link useRoomPresence} hands back. */
export interface RoomPresence {
  /** Tell the room where I am. A no-op for a recipe that has no room. */
  readonly send: (at: PresenceAt) => void;
  /** When the room last carried a frame from `userId` (`sync/room-sources.ts`). */
  readonly lastHeard: (userId: string) => number | undefined;
}

/**
 * Speak to `recipeId`'s room about where I am.
 *
 * The room is looked up on every call rather than once, because the editor
 * mounts before a shared recipe's source exists on a cold start. Stable for a
 * recipe, so it can sit in an effect's dependencies.
 */
export function useRoomPresence(recipeId: string): RoomPresence {
  const routed = use(WorkspaceContext)?.store ?? null;
  return useMemo(
    () => ({
      send: (at) => routed?.room(recipeId)?.sendPresence(at),
      lastHeard: (userId) => routed?.room(recipeId)?.lastHeard(userId),
    }),
    [routed, recipeId],
  );
}

/**
 * My reference to `recipeId` if it is a shared recipe, else `undefined`
 * (PLAN-COLLAB A2).
 *
 * What is *mine* about a shared recipe: my role and my equipment override
 * (D-CO3). A private recipe has no reference, which is how a screen tells the
 * two apart without asking the rooms.
 */
export function useSharedRecipeRef(recipeId: string): SharedRecipeRef | undefined {
  const selector = useCallback(
    (state: WorkspaceState) => state.sharedRecipes[recipeId],
    [recipeId],
  );
  return useWorkspaceSelector(selector);
}

/** False until the saved workspace has been read back. */
export function useHydrated(): boolean {
  return useWorkspaceContext().hydrated;
}

/**
 * The same flag, or `null` where there is no provider at all.
 *
 * {@link useHydrated} throws outside one, which is right for a component that
 * cannot work without a store. A reader that only wants to know whether there
 * is anything to wait for needs that third answer instead of an exception —
 * see `useWorkspaceSettled`.
 */
export function useMaybeHydrated(): boolean | null {
  return use(WorkspaceContext)?.hydrated ?? null;
}

/** Whose workspace is open: a user id, or `null` for the signed-out one. */
export function useWorkspaceIdentity(): string | null {
  return useWorkspaceContext().identity;
}

/**
 * Subscribe to a slice of the workspace.
 *
 * @param selector - Must be referentially stable (`useCallback` it, or define it
 *   at module scope). The cache keys on it as well as on the workspace state,
 *   so a selector rebuilt on every render recomputes on every render — which is
 *   correct, but pays for an answer it already had.
 * @param isEqual - Optional; use it when the selector derives a fresh object or
 *   array each call.
 */
export function useWorkspaceSelector<T>(
  selector: (state: WorkspaceState) => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
): T {
  const store = useWorkspaceStore();
  const cache = useRef<{
    state: WorkspaceState;
    selector: (state: WorkspaceState) => T;
    value: T;
  } | null>(null);

  const getSnapshot = useCallback(() => {
    const state = store.getState();
    const previous = cache.current;
    // A cached answer is an answer to a *question* about a *state*, so both
    // halves are the key. Keying on the state alone was a real bug twice: a
    // page handed a new selector — a route param moving from one recipe to the
    // next — was answered out of the cache with the recipe before it, and went
    // on drawing it at the new address until something else changed the
    // workspace. The stability contract above is what keeps this cheap: a
    // selector that has not changed still takes the shortcut, so nothing
    // recomputes until the question itself does.
    if (previous !== null && previous.state === state && previous.selector === selector) {
      return previous.value;
    }
    const next = selector(state);
    if (previous !== null && isEqual(previous.value, next)) {
      cache.current = { state, selector, value: previous.value };
      return previous.value;
    }
    cache.current = { state, selector, value: next };
    return next;
  }, [store, selector, isEqual]);

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}

/** Element-wise `Object.is` over two arrays. */
export function shallowArrayEqual<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((item, index) => Object.is(item, b[index]));
}

/** Key-wise `Object.is` over two plain objects. */
function shallowObjectEqual<T extends Record<string, unknown>>(a: T, b: T): boolean {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => Object.is(a[key], b[key]));
}

// ---------------------------------------------------------------------------
// Slice hooks
// ---------------------------------------------------------------------------

const selectSettings = (state: WorkspaceState): WorkspaceSettings => state.settings;
// D-CO4: a recipe this account shared still counts (sharing moves it out of
// `recipes` into an owner reference), and a recipe shared *with* this account
// never does, whether or not the merged view has copied it back in.
const selectRecipeCount = (state: WorkspaceState): number => capRecipeCount(state);
const selectRecipesById = (state: WorkspaceState) => state.recipes;
const selectEquipmentProfiles = (state: WorkspaceState) => state.equipmentProfiles;
const selectInventory = (state: WorkspaceState) => state.inventory;

/** Workspace settings. Stable while nothing under `settings` changes. */
export function useSettings(): WorkspaceSettings {
  return useWorkspaceSelector(selectSettings);
}

/**
 * Exported for the handful of callers that read units *outside* a render — the
 * ingredients route sizes a new row from `store.getState()` in a click handler,
 * and re-deriving `settings.units` there would be a second definition of where
 * units live.
 */
export const selectUnits = (state: WorkspaceState): UnitPreferences => state.settings.units;

/**
 * Unit *display* preferences — **value-stable**, not merely identity-stable.
 *
 * The sync client answers every ack and broadcast with `replaceState(replay(…))`,
 * a full replay whose result is a brand-new object tree; so does the cross-tab
 * snapshot reload. Selecting `settings.units` by identity made every one of
 * those a "units changed" to anything keyed on this hook — and the charts bake
 * unit formatters into their canvas at construction, so a second tab's idle
 * sync chatter was rebuilding this tab's charts every few seconds, resetting
 * hover and zoom mid-gesture. Units are a flat object of enum strings; comparing
 * the values is exact and turns identity churn back into what it is: nothing.
 */
export function useUnits(): UnitPreferences {
  return useWorkspaceSelector(selectUnits, shallowObjectEqual);
}

/** How many recipes exist — the sidebar's trial counter. */
export function useRecipeCount(): number {
  return useWorkspaceSelector(selectRecipeCount);
}

/**
 * One recipe by id, or `undefined`.
 *
 * The hot path: this is what every editor panel subscribes to, and it changes
 * identity only when a command actually touched that recipe.
 */
export function useRecipe(recipeId: string): RecipeDoc | undefined {
  const selector = useCallback((state: WorkspaceState) => state.recipes[recipeId], [recipeId]);
  return useWorkspaceSelector(selector);
}

/** Every recipe, newest edit first. */
export function useRecipeList(): readonly RecipeDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    return Object.values(state.recipes).sort((a, b) => b.updatedAt - a.updatedAt);
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/**
 * Every equipment profile of this workspace's, built-ins first, then by name.
 *
 * A shared recipe's default (`se.<recipeId>`) is in the merged table so the
 * recipe can resolve it, and is kept out of this list (PLAN-COLLAB C2): it is
 * the room's, not mine, and the Equipment page and the pickers are mine.
 * {@link useEquipmentProfilesById} keeps it, for lookups.
 */
export function useEquipmentProfiles(): readonly EquipmentProfileDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    return Object.values(state.equipmentProfiles)
      .filter((profile) => !isSharedEquipmentId(profile.id))
      .sort((a, b) => Number(b.builtin) - Number(a.builtin) || naturalCompare(a.name, b.name));
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/**
 * Every saved mash schedule, built-ins first, then by name.
 *
 * Same ordering as the equipment list, and for the same reason: the shipped set
 * is the vocabulary a brewer is meant to learn, so it goes at the top even once
 * they have saved their own.
 */
export function useMashProfiles(): readonly MashProfileDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    return Object.values(state.mashProfiles).sort(
      (a, b) => Number(b.builtin) - Number(a.builtin) || naturalCompare(a.name, b.name),
    );
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/** Every saved fermentation schedule, built-ins first, then by name. */
export function useFermentationProfiles(): readonly FermentationProfileDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    return Object.values(state.fermentationProfiles).sort(
      (a, b) => Number(b.builtin) - Number(a.builtin) || naturalCompare(a.name, b.name),
    );
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/**
 * Every saved additions profile, by name.
 *
 * Sorted the same way as the schedules. None are built in today, so the first
 * key never separates anything, but it keeps the ordering rule in one shape.
 */
export function useMiscProfiles(): readonly MiscProfileDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    return Object.values(state.miscProfiles).sort(
      (a, b) => Number(b.builtin) - Number(a.builtin) || naturalCompare(a.name, b.name),
    );
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/**
 * One batch by id, or `undefined`.
 *
 * The brew-day view's hot path, and the reason brew-day survives a tablet
 * sleeping mid-boil: every tick is a command, so the subscription re-renders
 * from synced state rather than from anything held in a component.
 */
export function useBatch(batchId: string): BatchDoc | undefined {
  const selector = useCallback((state: WorkspaceState) => state.batches[batchId], [batchId]);
  return useWorkspaceSelector(selector);
}

/**
 * Every batch, most recently brewed first.
 *
 * Sorted on `brewedAt` rather than `updatedAt` because a brew log is a
 * chronology: a batch whose notes were edited today still belongs where it was
 * brewed. Batches with no brew date yet (still `planned`) sort to the top,
 * which is where a batch you are about to brew wants to be, and ties break on
 * `createdAt` so the order is total and stable.
 */
export function useBatchList(): readonly BatchDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    return Object.values(state.batches).sort(
      (a, b) =>
        (b.brewedAt ?? Number.POSITIVE_INFINITY) - (a.brewedAt ?? Number.POSITIVE_INFINITY) ||
        b.createdAt - a.createdAt,
    );
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/** Every batch of one recipe, newest first — the editor's "brewed 3 times" affordance. */
export function useBatchesOfRecipe(recipeId: string): readonly BatchDoc[] {
  const selector = useCallback(
    (state: WorkspaceState) =>
      Object.values(state.batches)
        .filter((batch) => batch.recipeId === recipeId)
        .sort((a, b) => b.createdAt - a.createdAt),
    [recipeId],
  );
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/**
 * One blending session by id, or `undefined`.
 *
 * The blender's hot path: every NumberField commit is a `blend.update`, so the
 * page re-renders from the document rather than from local calculator state.
 */
export function useBlend(blendId: string): BlendDoc | undefined {
  const selector = useCallback((state: WorkspaceState) => state.blends[blendId], [blendId]);
  return useWorkspaceSelector(selector);
}

/** Every blending session, newest edit first. */
export function useBlendList(): readonly BlendDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    return Object.values(state.blends).sort((a, b) => b.updatedAt - a.updatedAt);
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/**
 * One journal by id, or `undefined`.
 *
 * The journal page's hot path, and the blend page's argument applies: every
 * block edit is a `journal.setBlock`, so the page re-renders from the document
 * rather than from local state and a block can never show something the
 * workspace does not hold.
 */
export function useJournal(journalId: string): JournalDoc | undefined {
  const selector = useCallback((state: WorkspaceState) => state.journals[journalId], [journalId]);
  return useWorkspaceSelector(selector);
}

/** Every journal, newest edit first. */
export function useJournalList(): readonly JournalDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    return Object.values(state.journals).sort((a, b) => b.updatedAt - a.updatedAt);
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/**
 * Every journal with a block about one batch, newest edit first (D-M1).
 *
 * Any number of journals may be about the same batch — a split batch is two
 * stories — so this is a list rather than an option, and the batch page reads
 * it to offer the way in. What counts as being about a batch is a block that
 * names it: a journal with a *note* about a batch and no block for it is not
 * that batch's journal, which is right, because the page shows what is on it.
 */
export function useJournalsOfBatch(batchId: string): readonly JournalDoc[] {
  const selector = useCallback(
    (state: WorkspaceState) =>
      Object.values(state.journals)
        .filter((journal) => journalReferences(journal).batchIds.includes(batchId))
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [batchId],
  );
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/**
 * Every journal with a block about one blending session, newest edit first.
 *
 * {@link useJournalsOfBatch}'s twin, and what makes provenance run both ways
 * for a blend as D-BL5 asks it to for a batch: the blend page lists the
 * journals whose blocks name it, and offers to start one.
 */
export function useJournalsOfBlend(blendId: string): readonly JournalDoc[] {
  const selector = useCallback(
    (state: WorkspaceState) =>
      Object.values(state.journals)
        .filter((journal) => journalReferences(journal).blendIds.includes(blendId))
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [blendId],
  );
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/**
 * The whole cupboard, keyed by id.
 *
 * The raw table rather than a sorted list because that is what the availability
 * check takes, and because it changes identity only when a command touched an
 * item — so a recipe editor subscribed to it re-renders its chips on a stock
 * movement and on nothing else.
 */
export function useInventory(): WorkspaceState['inventory'] {
  return useWorkspaceSelector(selectInventory);
}

const selectCustomIngredientTable = (state: WorkspaceState) => state.customIngredients;

/**
 * The custom-ingredient table itself, for the matcher.
 *
 * {@link useCustomIngredients} sorts the table into a list for the pages that
 * render it; the availability check wants the record, keyed by id, to resolve
 * a row's link (D-IL8), and wants it identity-stable so a keystroke in an
 * unrelated recipe re-runs nothing.
 */
export function useCustomIngredientTable(): WorkspaceState['customIngredients'] {
  return useWorkspaceSelector(selectCustomIngredientTable);
}

/**
 * Every custom fermentable, hop, yeast and misc, name-sorted.
 *
 * The ingredients page and the add-ingredient picker both subscribe to this
 * list; grouping by kind is cheap enough to do at the call site.
 */
export function useCustomIngredients(): readonly CustomIngredientDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    return Object.values(state.customIngredients).sort(
      naturalCompareBy(
        (entry) => entry.name,
        (entry) => entry.id,
      ),
    );
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/**
 * Every stocked lot, grouped kind by kind and then by name.
 *
 * Sorted by name rather than by when it was added: the inventory page is a
 * shelf, and a brewer looking for their Citra scans alphabetically. Ties break
 * on `createdAt` so two lots of the same hop stay in the order they will be
 * consumed in.
 */
export function useInventoryList(): readonly InventoryItemDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    const order: Record<InventoryItemDoc['kind'], number> = {
      fermentable: 0,
      hop: 1,
      yeast: 2,
      misc: 3,
    };
    return Object.values(state.inventory).sort(
      (a, b) =>
        order[a.kind] - order[b.kind] ||
        naturalCompare(a.name, b.name) ||
        a.createdAt - b.createdAt,
    );
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/** One stocked lot by id, or `undefined`. */
export function useInventoryItem(itemId: string): InventoryItemDoc | undefined {
  const selector = useCallback((state: WorkspaceState) => state.inventory[itemId], [itemId]);
  return useWorkspaceSelector(selector);
}

/** The raw recipe table — for the command palette, which wants everything at once. */
export function useRecipesById(): WorkspaceState['recipes'] {
  return useWorkspaceSelector(selectRecipesById);
}

/** The raw equipment table. */
export function useEquipmentProfilesById(): WorkspaceState['equipmentProfiles'] {
  return useWorkspaceSelector(selectEquipmentProfiles);
}

/**
 * Every saved water profile, built-ins first, then by name.
 *
 * Same ordering rule as the equipment profiles, and for the same reason: the
 * shipped analyses are what a new user picks from, and a profile they typed
 * themselves is something they will recognise by name whichever end it sits at.
 * Built-ins keep their declaration order (RO first, then the cities), which the
 * document module chose deliberately.
 */
export function useWaterProfiles(): readonly SavedWaterProfileDoc[] {
  const selector = useCallback((state: WorkspaceState) => {
    const all = Object.values(state.waterProfiles);
    const builtins = all.filter((profile) => profile.builtin);
    const mine = all
      .filter((profile) => !profile.builtin)
      .sort((a, b) => naturalCompare(a.name, b.name));
    return [...builtins, ...mine];
  }, []);
  return useWorkspaceSelector(selector, shallowArrayEqual);
}

/** One saved water profile by id, or `undefined`. */
export function useWaterProfile(profileId: string): SavedWaterProfileDoc | undefined {
  const selector = useCallback(
    (state: WorkspaceState) => state.waterProfiles[profileId],
    [profileId],
  );
  return useWorkspaceSelector(selector);
}

/** Undo/redo availability and labels. */
export function useHistory() {
  const store = useWorkspaceStore();
  return useSyncExternalStore(store.subscribe, store.getHistory, store.getHistory);
}
